import { useCallback, useEffect, useRef, useState } from "react";

/** Long enough to bridge a dragleave/dragover pair between two children. */
const LEAVE_DEBOUNCE_MS = 50;

export interface DragHighlight {
  /** True while a drag the zone accepts is over it. */
  isActive: boolean;
  /** Call from dragover: lights the highlight and cancels a pending leave. */
  enter: () => void;
  /** Call from dragleave: clears the highlight unless a dragover follows at once. */
  leave: () => void;
  /** Clear immediately — after a drop, or when the drag is refused. */
  clear: () => void;
}

/**
 * The highlight state of a drop zone, without the flicker.
 *
 * `dragleave` fires every time the pointer crosses from one child of the zone
 * into another, immediately followed by a `dragover` on the new child. Clearing
 * on the leave paints the highlight off and on again for one frame; clearing
 * only when `currentTarget === target` leaves it stuck on whenever the final
 * leave comes from a child. A short debounce that the next dragover cancels
 * handles both.
 *
 * A drop that a nested target handles with `stopPropagation` never reaches the
 * zone's own drop handler, so the highlight would stay lit after it. Capture-
 * phase `drop`/`dragend` listeners on the window run before any handler can
 * stop the event and clear it regardless.
 */
export function useDragHighlight(): DragHighlight {
  const [isActive, setIsActive] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelLeave = useCallback(() => {
    if (leaveTimer.current === null) return;
    clearTimeout(leaveTimer.current);
    leaveTimer.current = null;
  }, []);

  const clear = useCallback(() => {
    cancelLeave();
    setIsActive(false);
  }, [cancelLeave]);

  const enter = useCallback(() => {
    cancelLeave();
    setIsActive(true);
  }, [cancelLeave]);

  const leave = useCallback(() => {
    cancelLeave();
    leaveTimer.current = setTimeout(() => {
      leaveTimer.current = null;
      setIsActive(false);
    }, LEAVE_DEBOUNCE_MS);
  }, [cancelLeave]);

  useEffect(() => {
    if (!isActive) return;
    window.addEventListener("drop", clear, true);
    window.addEventListener("dragend", clear, true);
    return () => {
      window.removeEventListener("drop", clear, true);
      window.removeEventListener("dragend", clear, true);
    };
  }, [isActive, clear]);

  useEffect(() => cancelLeave, [cancelLeave]);

  return { isActive, enter, leave, clear };
}
